import { useRef } from 'react'

interface Props {
  value: string
  onChange: (v: string) => void
  /** Quantidade de dígitos (padrão 6). */
  length?: number
  disabled?: boolean
  autoFocus?: boolean
  /** Disparado quando todos os dígitos foram preenchidos. */
  onComplete?: (v: string) => void
}

/**
 * Campo de código OTP em caixas individuais, com:
 *  - auto-avanço ao digitar e auto-retrocesso no Backspace
 *  - colar (paste) o código inteiro em qualquer caixa
 *  - navegação por setas
 *  - inputMode numérico + autocomplete "one-time-code" (preenchimento automático)
 */
export function OtpInput({ value, onChange, length = 6, disabled, autoFocus, onComplete }: Props) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const digits = Array.from({ length }, (_, i) => value[i] ?? '')

  const focusAt = (i: number) => refs.current[Math.max(0, Math.min(i, length - 1))]?.focus()

  const emit = (next: string) => {
    const clean = next.replace(/\D/g, '').slice(0, length)
    onChange(clean)
    if (clean.length === length) onComplete?.(clean)
    return clean
  }

  const handleChange = (i: number, raw: string) => {
    const d = raw.replace(/\D/g, '')
    if (!d) {
      const arr = value.split('')
      arr[i] = ''
      emit(arr.join(''))
      return
    }
    // colou/multiplos dígitos a partir desta caixa
    if (d.length > 1) {
      const next = emit(value.slice(0, i) + d)
      focusAt(next.length)
      return
    }
    const arr = value.padEnd(length, ' ').split('')
    arr[i] = d
    emit(arr.join('').replace(/ /g, ''))
    focusAt(i + 1)
  }

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const arr = value.split('')
      if (arr[i]) {
        arr[i] = ''
        emit(arr.join(''))
      } else if (i > 0) {
        arr[i - 1] = ''
        emit(arr.join(''))
        focusAt(i - 1)
      }
    } else if (e.key === 'ArrowLeft') {
      focusAt(i - 1)
    } else if (e.key === 'ArrowRight') {
      focusAt(i + 1)
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const d = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!d) return
    const next = emit(d)
    focusAt(next.length)
  }

  return (
    <div className="flex items-center justify-center gap-2" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el }}
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={`Dígito ${i + 1}`}
          className="h-12 w-11 rounded-lg border border-gray-300 bg-white text-center text-xl font-semibold text-gray-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 disabled:bg-gray-50 disabled:text-gray-400"
        />
      ))}
    </div>
  )
}

export default OtpInput
